import { createSelector } from 'reselect'
import _ from 'lodash'
import { AllHtmlEntities } from 'html-entities'
import { INTROBILD, INTRO_GROUPS } from '../constants'
import layerComposition from '../components/Map/style/LayerComposition'

const entities = new AllHtmlEntities()

// State slice helper
export const text = state => state.text
export const timeline = state => state.timeline
export const companies = state => state.companies
export const map = state => state.map
export const status = state => state.status
export const system = state => state.system

export const stateSelector = state => state

// Map helper
export const getViewport = state => state.map.viewport
export const getBounds = state => state.map.bounds
export const getHistMapBounds = state => state.map.histMapBounds
export const getMapTransition = state => state.map.transition

// Timeline helper
export const getCurrentYear = state => state.timeline.current
export const getPhases = state => state.timeline.phases
export const getSelectedTopicID = state => state.timeline.selectedTopicID
export const getTimelineEvents = state => state.timeline.results
export const getPeriodLayerVisibility = state => state.timeline.showPeriodLayer

// Text helper
export const getTitle = state => state.text.title
export const getSubtitle = state => state.text.subtitle
export const getPhasesText = state => state.text.phases

// Companies helper
export const getCompanyItems = state => state.companies.items
export const getVisibleProfiles = state => state.companies.showProfile
export const getExpandedProfile = state => state.companies.expandedProfile

// System helper
const getLatestInteraction = state => state.system.latestInteraction

// STATUS

/**
 * Returns all companies keyed by counter, company name, and paths with data problems
 */
export const namedStatusSelector = createSelector(
  status,
  status =>
    _.keyBy(
      status.items,
      x =>
        `${x.id}-${x.name} [${_.uniq(
          x.valid.map(v => v.path.split('.')[0])
        ).join(',')}]`
    )
)

// SYSTEM
export const activeSelector = createSelector(
  getLatestInteraction,
  interaction => !!interaction
)

export const showLegendSelector = createSelector(
  activeSelector,
  getPeriodLayerVisibility,
  (active, periodIsExpanded) => active && !periodIsExpanded
)

// TEXT

/**
 * Returns the title and subtitle of the application
 */
export const textSelector = createSelector(
  getTitle,
  getSubtitle,
  (title, subtitle) => ({
    title,
    subtitle,
  })
)

/**
 * Returns title, subtitle, and text of all phases
 */
export const phaseTextSelector = createSelector(
  getPhasesText,
  phases =>
    _.mapValues(phases, phaseText => ({
      ...phaseText,
      text: entities.decode(phaseText.text),
    }))
)

// COMPANIES
const getCurrentAddress = createSelector(
  x => x.company,
  x => x.year,
  (company, year) => {
    const {
      locations,
      start,
      end,
      position,
      historicalAddress: historical,
      currentAddress: current,
    } = company

    if (locations && year) {
      const currentLocations = locations.filter(
        ({ start, end }) => start <= year && end >= year
      )

      // Take the first matching location for the current year
      if (currentLocations.length > 0) return _.last(currentLocations)

      // If company did not exist in current year return first known address
      if (year < start) return _.first(_.sortBy(locations, ['start', 'end']))

      // If company did not exist anymore in current year return last known address
      if (year > end) return _.last(_.sortBy(locations, ['end', 'start']))
    }

    // If no locations record was found in the current year return the main address
    return {
      start,
      end,
      years: end - start,
      names: { historical, current },
      position: {
        lat: position.lat,
        lng: position.lng,
      },
    }
  }
)

const sortImages = images => {
  const byCategory = _.sortBy(images, 'type')
  const byTop = _.sortBy(byCategory, 'top')
  const byIntro = _.sortBy(byTop, image => (image.type === INTROBILD ? -1 : 1))

  return byIntro
}

export const companiesWithImagesSelector = createSelector(
  getCompanyItems,
  companies =>
    _.values(companies).filter(
      company => company.images && company.images.length > 0
    )
)

export const companiesAllSelector = createSelector(
  getCompanyItems,
  companies =>
    _.values(companies).map(company => {
      const { images, addresses, locations, currentAddress } = company

      return {
        ...company,
        originalAddress: currentAddress,
        images: images && images.length > 0 ? sortImages(images) : null,
        addressesOriginal: addresses,
        currentAddress: getCurrentAddress({ company }),
        addresses: !locations
          ? []
          : locations.map(({ start, end, names }) => {
              const { historical, current } = names
              return {
                date: `${start} – ${end}`,
                text:
                  historical === current
                    ? historical
                    : `${historical} (heute ${current})`,
              }
            }),
      }
    })
)

export const companiesByYearAndTopicSelector = createSelector(
  companiesAllSelector,
  getCurrentYear,
  getSelectedTopicID,
  getTimelineEvents,
  (companies, year, selectedTopicID, events) =>
    companies.map(company => {
      const { start, end, groups } = company
      const topic = selectedTopicID && events[selectedTopicID]
      const existsInTopic =
        !!topic && (start <= topic.end && end >= topic.start)
      const existsInYear = start <= year && end >= year
      const matchesTopic =
        selectedTopicID && groups.indexOf(selectedTopicID) > -1

      return {
        ...company,
        existsInTopic,
        existsInYear,
        matchesTopic,
        currentAddress: getCurrentAddress({ company, year }),
      }
    })
)

/**
 * Returns all companies filtered by the current year and topic (if selected)
 * Adds historical location data to each company entry
 */
export const companiesFilteredSelector = createSelector(
  companiesByYearAndTopicSelector,
  getSelectedTopicID,
  (companies, selectedTopicID) => {
    return selectedTopicID
      ? _.filter(companies, 'existsInTopic')
      : _.filter(companies, 'existsInYear')
  }
)

export const introImagesSelector = createSelector(
  companiesAllSelector,
  companies =>
    companies
      .filter(({ images }) => images && images.some(({ intro }) => intro))
      .map(company => ({
        ...company,
        introImage: company.images.find(i => i.intro),
      }))
)

export const companiesByActiveModeSelector = createSelector(
  companiesAllSelector,
  companiesFilteredSelector,
  activeSelector,
  (all, filtered, active) => (active ? filtered : all)
)

export const introImageGroupSelector = createSelector(
  introImagesSelector,
  companies => {
    const gRe = INTRO_GROUPS.map(group => new RegExp(group))
    return _.toPairs(
      _.groupBy(companies, ({ introImage }) => {
        const index = gRe.findIndex(re => re.test(introImage.type))
        return INTRO_GROUPS[index]
      })
    ).map(([name, items]) => ({ name, items }))
  }
)

/**
 * Returns all companies with open profiles sorted by opening date
 */
export const openProfilesSelector = createSelector(
  getVisibleProfiles,
  getExpandedProfile,
  companiesFilteredSelector,
  getPeriodLayerVisibility,
  (showProfile, companyExpanded, companies, layerIsExpanded) => {
    if (layerIsExpanded) return []
    const showProfileIsTrue = _.pickBy(showProfile)
    return _.sortBy(
      _.values(companies)
        .filter(({ id }) => {
          if (companyExpanded) {
            return id === companyExpanded
          }
          return showProfileIsTrue[id]
        })
        .map(company => ({
          ...company,
          lastOpened: showProfile[company.id],
        })),
      'lastOpened'
    )
  }
)

// TIMELINE

/**
 * Returns whether the period layer should be shown or not
 */
export const periodLayerVisibilitySelector = createSelector(
  getPeriodLayerVisibility,
  activeSelector,
  (show, active) => show && active
)

const phaseSelector = createSelector(
  getCurrentYear,
  getPhases,
  (current, phases) =>
    _.pickBy(phases, ({ start, end }) => start <= current && end >= current)
)

const phaseIDSelector = createSelector(
  phaseSelector,
  phase => _.keys(phase)[0]
)

/**
 * Returns the selected phase on the timeline
 */
export const currentPhaseSelector = createSelector(
  getPhases,
  phaseIDSelector,
  phaseTextSelector,
  (phases, phaseID, texts) => ({
    ...phases[phaseID],
    ...texts[phaseID],
  })
)

/**
 * Returns all timeline data, filters out topics without associated
 * companies, and adds steps; adds to each topic the corresponding
 * stats (branch distribution) and number of assigned companies
 */
export const timelineSelector = createSelector(
  timeline,
  getCompanyItems,
  (timeline, companies) => {
    const { results } = timeline

    const enhancedResults = _.mapValues(results, topic => {
      // Get companies that are attached to the topic
      const topicCompanies = _.values(companies).filter(
        company => company.groups.indexOf(topic.id) > -1
      )

      // Count attached companies
      const { length } = topicCompanies
      const groupedCompanies = _.groupBy(topicCompanies, 'branch')

      // Add company branch share to topic
      const stats = _.mapValues(groupedCompanies, group =>
        _.round(group.length / length, 2)
      )
      return { ...topic, stats, length }
    })

    return {
      ...timeline,
      // TODO: do no spread timeline, pick consumed props
      topics: _.values(enhancedResults).filter(x => x.length),
    }
  }
)

/**
 * Returns the selected Topic
 */
export const selectedTopicSelector = createSelector(
  getSelectedTopicID,
  getTimelineEvents,
  companiesFilteredSelector,
  (topicID, events, companies) => {
    if (topicID) {
      const selection = _.take(_.filter(companies, 'images'), 7)
      return {
        ...events[topicID],
        images: selection.map(c => c.images[0]),
      }
    }
    return null
  }
)

/**
 * If no topic is selected it returns the current year, otherwise the topic's year range (start - end)
 */
export const currentRangeSelector = createSelector(
  getSelectedTopicID,
  getTimelineEvents,
  getCurrentYear,
  (id, results, current) =>
    id ? `${results[id].start}-${results[id].end}` : current.toString()
)

// MAP

/**
 * Returns the path base for the current historical map
 */
export const tilesPathSelector = createSelector(
  getCurrentYear,
  currentPhaseSelector,
  (year, phase) => (!_.inRange(year, 1945, 1949) ? phase.map : 1946)
)

/**
 * Returns GeoJSON to visualize company networks
 */
export const relationsSelector = createSelector(
  companiesFilteredSelector,
  getTimelineEvents,
  getSelectedTopicID,
  (companies, topics, topicID) => {
    const empty = {
      type: 'FeatureCollection',
      features: [],
    }
    const topic = topics[topicID]

    if (!topicID || topic.type !== 'Firmengruppe') return empty
    const root = _.values(companies).find(
      ({ related, matchesTopic }) => matchesTopic && related
    )

    const children = _.values(companies).filter(
      ({ related, matchesTopic }) => matchesTopic && !related
    )

    if (root && children.length > 0) {
      const features = children.map(c => {
        const { lat: cLat, lng: cLng } = c.currentAddress.position
        const { lat: rootLat, lng: rootLng } = root.currentAddress.position

        return {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [[cLng, cLat], [rootLng, rootLat]],
          },
        }
      })

      return {
        type: 'FeatureCollection',
        features,
      }
    }

    return empty
  }
)

/**
 * Returns the outer points/coordinates along the map
 */
export const histMapBoundsSelector = createSelector(
  tilesPathSelector,
  getHistMapBounds,
  (path, bounds) => ({
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [bounds[path]],
          },
        },
      ],
    },
  })
)

/**
 * Returns the map style object
 */
export const mapStyleSelector = createSelector(
  relationsSelector,
  tilesPathSelector,
  histMapBoundsSelector,
  activeSelector,
  (relations, tilesPath, bounds, active) => {
    const layers = layerComposition(
      relations.features.length > 0,
      tilesPath,
      active
    )
    let base = {
      version: 8,
      sources: _.pickBy({
        osm: {
          type: 'vector',
          tiles: [`${window.origin}/vector-tiles/{z}/{x}/{y}.pbf`],
          attribution: 'Map data © OpenStreetMap',
        },
        bounds: tilesPath ? bounds : null,
        histMap: tilesPath
          ? {
              type: 'raster',
              tiles: [
                `/historical-tiles/${tilesPath}/${tilesPath}/{z}/{x}/{y}.png`,
              ],
              tileSize: 512,
            }
          : null,
      }),
      sprite: `${window.origin}/sprites/sprites`,
      glyphs: `/font/fira-sans-pbf/{fontstack}/{range}.pbf`,
      layers,
    }

    if (relations) {
      base = {
        ...base,
        sources: {
          ...base.sources,
          relations: {
            type: 'geojson',
            data: relations,
          },
        },
      }
    }

    return base
  }
)
